import { useState, useEffect, useRef } from "react";
import API from "../../services/api";
import AdminLayout from "../../layouts/AdminLayout";
import { Users, UserCog, Settings, User, RefreshCw, Search, X, Plus, Pencil, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";

const FONT_LINK = "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap";
const T = {
  violet: "#5C2D91",
  violetMid: "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red: "#C8102E",
  redLight: "#FCEAED",
  orange: "#E8640A",
  orangeLight: "#FEF0E6",
  gold: "#C87800",
  goldLight: "#FEF5DC",
  green: "#1A7A42",
  greenLight: "#E2F5EC",
  teal: "#0D9488",
  tealLight: "#F0FDFA",
  blue: "#1D4ED8",
  blueLight: "#EFF6FF",
  bg: "#F4F0FA",
  surface: "#FFFFFF",
  surfaceAlt: "#FAF8FD",
  dark: "#1A0A2E",
  muted: "#6B5A8A",
  faint: "#C4B8DC",
  border: "rgba(92,45,145,0.12)",
  borderSolid: "#E8E0F5",
  shadow: "0 2px 12px rgba(92,45,145,0.06)",
  shadowMd: "0 4px 20px rgba(92,45,145,0.12)",
};

const ROLE_CFG = {
  admin: { grad: `linear-gradient(135deg,${T.violet},${T.violetMid})`, color: T.violet, bg: T.violetLight },
  manager: { grad: `linear-gradient(135deg,${T.blue},#3B82F6)`, color: T.blue, bg: T.blueLight },
  engineer: { grad: `linear-gradient(135deg,${T.teal},#14B8A6)`, color: T.teal, bg: T.tealLight },
  client: { grad: `linear-gradient(135deg,${T.red},#E02040)`, color: T.red, bg: T.redLight },
};
const roleCfg = (r = "") => ROLE_CFG[(r || "").toLowerCase()] || {
  grad: `linear-gradient(135deg,${T.orange},#F5830A)`, color: T.orange, bg: T.orangeLight,
};

const getDisplayRole = (user) => {
  const role = (user.role || "").toLowerCase();
  const dash = (user.dashboard_type || "").toLowerCase();
  if (role === "engineer" && dash === "admin") return "Manager";
  return user.role;
};

const Avatar = ({ name = "", role = "", size = 38, onClick, title, clickable = false, loading = false }) => {
  const cfg = roleCfg(role);
  const init = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => clickable && setHov(true)}
      onMouseLeave={() => clickable && setHov(false)}
      title={title || (clickable ? `Click to login as ${name}` : name)}
      style={{
        width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: cfg.grad,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.36, fontWeight: 700, color: "#fff",
        boxShadow: hov && clickable ? `0 0 0 3px ${T.violetLight}, 0 4px 12px ${cfg.color}60` : `0 2px 8px ${cfg.color}40`,
        cursor: clickable ? "pointer" : "default",
        transform: hov && clickable ? "scale(1.12)" : "scale(1)",
        transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
        userSelect: "none",
        position: "relative",
      }}
    >
      {loading ? (
        <span style={{
          display: "inline-block", width: 14, height: 14,
          border: "2px solid rgba(255,255,255,0.4)",
          borderTopColor: "#fff", borderRadius: "50%",
          animation: "um-spin 0.7s linear infinite",
        }} />
      ) : (
        init
      )}
    </div>
  );
};

const inputSty = (focused) => ({
  width: "100%", borderRadius: 10,
  border: `1.5px solid ${focused ? T.violet : T.borderSolid}`,
  padding: "10px 14px", fontSize: 14, color: T.dark,
  background: focused ? T.surface : T.surfaceAlt,
  outline: "none", fontFamily: "Montserrat, sans-serif",
  boxSizing: "border-box", transition: "all 0.18s",
  boxShadow: focused ? `0 0 0 3px ${T.violetLight}` : "none",
});
const LabelSty = {
  fontSize: 11, fontWeight: 700, color: T.muted,
  textTransform: "uppercase", letterSpacing: "0.07em",
  display: "block", marginBottom: 6,
};

function SearchDropdown({ label, value, onChange, options = [], placeholder = "Search…" }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase()));

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

const select = (opt) => { setQ(opt); onChange(opt); setOpen(false); };

const handleKeyDown = (e) => {
  if (e.key === "Enter" && q.trim() && !filtered.includes(q.trim())) {
    onChange(q.trim());
    setOpen(false);
  }
};

  return (
    <div>
      <label style={LabelSty}>{label}</label>
      <div ref={ref} style={{ position: "relative" }}>
        <input type="text" value={q} placeholder={placeholder}
       onChange={e => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
onFocus={() => { setFocused(true); setOpen(true); }}
onBlur={() => setFocused(false)}
onKeyDown={handleKeyDown}
          style={inputSty(focused)}
        />
        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: T.faint, pointerEvents: "none" }}>▾</span>
        {open && filtered.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: T.surface, borderRadius: 10, border: `1.5px solid ${T.borderSolid}`, boxShadow: T.shadowMd, zIndex: 200, maxHeight: 180, overflowY: "auto" }}>
            {filtered.map(opt => (
              <div key={opt} onMouseDown={() => select(opt)}
                style={{ padding: "9px 14px", fontSize: 13, color: T.dark, cursor: "pointer", borderBottom: `1px solid ${T.borderSolid}`, background: opt === value ? T.violetLight : "transparent", fontWeight: opt === value ? 600 : 400 }}
                onMouseEnter={e => e.currentTarget.style.background = T.violetLight}
                onMouseLeave={e => e.currentTarget.style.background = opt === value ? T.violetLight : "transparent"}
              >{opt}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MultiSearchDropdown({ label, values = [], onChange, options = [], placeholder = "Search…" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  const filtered = options.filter(o => !values.includes(o) && o.toLowerCase().includes(q.toLowerCase()));

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const addValue = (opt) => {
    if (!values.includes(opt)) onChange([...values, opt]);
    setQ("");
    setOpen(false);
  };

  const removeValue = (opt) => {
    onChange(values.filter(v => v !== opt));
  };

  return (
    <div>
      <label style={LabelSty}>{label}</label>
      <div ref={ref} style={{ position: "relative" }}>
        <div style={{
          ...inputSty(focused),
          display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
          minHeight: 42, padding: values.length ? "6px 10px" : "10px 14px",
        }}>
          {values.map(v => (
            <span key={v} style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: T.violetLight, color: T.violet,
              fontSize: 12, fontWeight: 600, borderRadius: 20,
              padding: "3px 6px 3px 10px",
            }}>
              {v}
              <span
                onClick={() => removeValue(v)}
                style={{
                  cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", width: 16, height: 16, borderRadius: "50%",
                  background: "rgba(92,45,145,0.15)",
                }}
              ><X size={10} /></span>
            </span>
          ))}
          <input
            type="text"
            value={q}
            placeholder={values.length ? "" : placeholder}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => { setFocused(true); setOpen(true); }}
            onBlur={() => setFocused(false)}
            style={{
              border: "none", outline: "none", background: "transparent",
              fontSize: 14, color: T.dark, flex: 1, minWidth: 80,
              fontFamily: "Montserrat, sans-serif",
            }}
          />
        </div>
        {open && filtered.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: T.surface, borderRadius: 10, border: `1.5px solid ${T.borderSolid}`, boxShadow: T.shadowMd, zIndex: 200, maxHeight: 180, overflowY: "auto" }}>
            {filtered.map(opt => (
              <div key={opt} onMouseDown={() => addValue(opt)}
                style={{ padding: "9px 14px", fontSize: 13, color: T.dark, cursor: "pointer", borderBottom: `1px solid ${T.borderSolid}` }}
                onMouseEnter={e => e.currentTarget.style.background = T.violetLight}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >{opt}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Toggle = ({ checked, onChange, label }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
    <div onClick={() => onChange(!checked)} style={{ width: 46, height: 26, borderRadius: 13, background: checked ? T.violet : T.borderSolid, position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 3, left: checked ? 23 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.18)", transition: "left 0.2s" }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 600, color: checked ? T.violet : T.muted }}>{label}</span>
  </label>
);
// const REGION_UNIT_MAP = {
//   "APTG+Kerala": ["AP CENTRAL","AP COASTAL","AP RAYALASEEMA","APTG KEY","KERALA NORTH","KERALA SOUTH","TG FACTORY","TG GEO","TG GOVT & UPC"],
//   "MPCGOD": ["Chattishgarh","Madhya Pradesh - 1","Madhya Pradesh - 2","MP Govt+Factory","Odissa"],
//   "Gujarat": ["Factory Gujarat","Govt Gujarat","North Gujarat","Saurashtra & Kutch","South Central Gujarat","RAJ 1","RAJ 2"],
//   "GOMUMBAI": ["WM LOC 1","DAKU","FACTORY","GOA","GoMum-Admixture","GOVT-GOMUM","MUMBAI KA","NAVI MUMBAI","SOUTH MUMBAI","TCM","WM LOC 2","WM UPC","WP -2"],
//   "NEWBJB": ["GOVT: WB+JB","Jharkhand","Bihar","KOLKATA METRO","NE 1","NE 2","WBUPC"],
//   "NORTH": ["DELHI","HRY 1","HRY 2","KPUNCH-1","KPUNCH-2","NOIDA","UK","UP EAST","UP WEST"],
//   "ROM": ["Factory-ROM N","Factory-ROM S","GOVT-ROM N","GOVT-ROM S","NASHIK","PUNE GEOGRAPHY","PUNE KEY ACCOUNT","PUNE UPC","Renner -ROM","VIDARBH"],
//   "SOUTH KT": ["GBR FACT","GBR KA","GBR N","GBR S","KT GOVT","ROK N","ROK S"],
//   "SOUTH TN": ["TN CENTRAL","TN FACT","TN GOVERNMENT","TN NORTH","TN SOUTH","TN WP"],
// };

const REGION_UNIT_MAP = {
  "APTG+Kerala": ["AP CENTRAL","AP COASTAL","AP RAYALASEEMA","APTG KEY","KERALA NORTH","KERALA SOUTH","TG FACTORY","TG GEO","TG GOVT & UPC"],
  "MPCGOD": ["Chattishgarh","Madhya Pradesh - 1","Madhya Pradesh - 2","MP Govt+Factory","Odissa"],
  "Gujarat": ["Factory Gujarat","Govt Gujarat","North Gujarat","Saurashtra & Kutch","South Central Gujarat","RAJ 1","RAJ 2"],
  "GOMUMBAI": ["WM LOC 1","DAKU","FACTORY","GOA","GoMum-Admixture","GOVT-GOMUM","MUMBAI KA","NAVI MUMBAI","SOUTH MUMBAI","TCM","WM LOC 2","WM UPC","WP -2"],
  "NEWBJB": ["GOVT: WB+JB","Jharkhand","Bihar","KOLKATA METRO","NE 1","NE 2","WBUPC"],
  "NORTH": ["DELHI","HRY 1","HRY 2","KPUNCH-1","KPUNCH-2","NOIDA","UK","UP EAST","UP WEST"],
  "ROM": ["Factory-ROM N","Factory-ROM S","GOVT-ROM N","GOVT-ROM S","NASHIK","PUNE GEOGRAPHY","PUNE KEY ACCOUNT","PUNE UPC","Renner -ROM","VIDARBH"],
  "SOUTH KT": ["GBR FACT","GBR KA","GBR N","GBR S","KT GOVT","ROK N","ROK S"],
  "SOUTH TN": ["TN CENTRAL","TN FACT","TN GOVERNMENT","TN NORTH","TN SOUTH","TN WP"],
};

const REGION_PLACEHOLDER = Object.keys(REGION_UNIT_MAP);

const ActionBtn = ({ title, onClick, color, bg, children, disabled = false }) => {
  const [hov, setHov] = useState(false);
  return (
    <button title={title} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: 34, height: 34, borderRadius: 10,
        border: `1.5px solid ${hov && !disabled ? color : T.borderSolid}`,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, flexShrink: 0, transition: "all 0.16s",
        background: hov && !disabled ? bg : T.surface,
        color: hov && !disabled ? color : T.muted,
        opacity: disabled ? 0.4 : 1,
        boxShadow: hov && !disabled ? `0 2px 8px ${color}30` : "none",
      }}
    >{children}</button>
  );
};

const MONTSERRAT = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');`;

export default function UserMaster() {

  const formRef = useRef(null);

  const [usersList, setUsersList] = useState([]);
  const [stats, setStats] = useState({ total: 0, managers: 0, engineers: 0, clients: 0 });
  const [rolesList, setRolesList] = useState([]);
// const [unitOptions, setUnitOptions] = useState([]); // populated once a region is chosen
  const [regionOptions, setRegionOptions] = useState(REGION_PLACEHOLDER);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
 const [role, setRole] = useState("engineer");
  const [address, setAddress] = useState("");
  const [isApprover, setIsApprover] = useState(false);
  const [unit, setUnit] = useState([]);
  // const [region, setRegion] = useState("");
  const [adminAccess, setAdminAccess] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [dashboardType, setDashboardType] = useState("");
  const [search, setSearch] = useState("");
  const [hov, setHov] = useState(null);
  const [roleFilter, setRoleFilter] = useState("All");
  const [foc, setFoc] = useState({});
  const [deleteModal, setDeleteModal] = useState({ open: false, userId: null });
  const [currentPage, setCurrentPage] = useState(1);
const ROWS_PER_PAGE = 10;
const [regionFilter, setRegionFilter] = useState("All");
const [unitFilter, setUnitFilter] = useState("All");

  const [saving, setSaving] = useState(false);
const [saveError, setSaveError] = useState("");
const [saveSuccess, setSaveSuccess] = useState("");
const [refreshing, setRefreshing] = useState(false);

  const fIn = (k) => () => setFoc(p => ({ ...p, [k]: true }));
  const fOut = (k) => () => setFoc(p => ({ ...p, [k]: false }));

  // auto-dismiss success toast after 4 seconds
  useEffect(() => {
    if (!saveSuccess) return;
    const t = setTimeout(() => setSaveSuccess(""), 4000);
    return () => clearTimeout(t);
  }, [saveSuccess]);

  const fetchSystemUsers = async () => {
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [listRes, rolesRes] = await Promise.all([
        API.get("/admin/users", config),
        API.get("/admin/roles", config),
      ]);
      if (listRes.data?.success) setUsersList(listRes.data.data || []);
      if (rolesRes.data?.success) setRolesList(rolesRes.data.data || []);
    } catch (err) { console.error("Users/roles fetch failed:", err); }

    try {
      const statsRes = await API.get("/admin/dashboard-status", config);
      if (statsRes.data?.success) {
        setStats({
          total: statsRes.data.total || 0,
          managers: statsRes.data.managers || 0,
          engineers: statsRes.data.activeEngineers || 0,
          clients: statsRes.data.clients || 0,
        });
      }
    } catch (err) { console.warn("Stats fetch failed:", err); }
  };

  useEffect(() => { fetchSystemUsers(); }, []);

  const handleSaveUser = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    // basic required fields
    if (!fullName.trim() || !email.trim() || !role) {
      setSaveError("Please fill in Name, Email, and Role fields.");
      return;
    }

    // validation change


    // Name Validation
if (!/^[A-Za-z ]+$/.test(fullName.trim())) {
    setSaveError("Name should contain only letters.");
    return;
}

// Email Validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
if (!emailRegex.test(email.trim())) {
    setSaveError("Please enter a valid email address.");
    return;
}

/////////////////////////////////////////////////////////////////////////////////
// Asian Paints Email Validation — 
if (
  role !== "client" &&
  email.trim().toLowerCase() !== "totalassureplus.com@gmail.com" &&
  !email.trim().toLowerCase().endsWith("@asianpaints.com")
) {
  setSaveError("Email must be a valid Asian Paints email (e.g. name@asianpaints.com)");
  return;
}

// Mobile Validation
if (!/^\d{10}$/.test(phone.trim())) {
    setSaveError("Mobile number must be exactly 10 digits.");
    return;
}

    // // role must be a valid option from the list
    // const validRoles = rolesList.map(r => r.title.toLowerCase());
    // if (validRoles.length && !validRoles.includes(role.toLowerCase())) {
    //   setSaveError("Please select a valid role from the dropdown.");
    //   return;
    // }

    setSaveError("");
    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      // Only admin role users should ever get adminAccess
//     const payload = {
//   name: fullName.trim(), phone: phone.trim(),
//   email: email.trim().toLowerCase(), role: role.toLowerCase(),
//   address: address.trim(), isApprover, unit, region,
//   adminAccess,                                  //   always send what was toggled
//   dashboardType: dashboardType || "engineer",    //   always trust the dropdown, role has no say
// };

const payload = {
  name: fullName.trim(), phone: phone.trim(),
  email: email.trim().toLowerCase(), role: role.toLowerCase(),
  address: address.trim(), isApprover,
  unit: unit.join(","),
  region: region.join(","),
  adminAccess,
  dashboardType: role.toLowerCase() === "manager" ? "admin" : (dashboardType || "engineer"),
};

      const wasEditing = !!editingId;

      if (editingId) {
        await API.put(`/admin/users/${editingId}`, payload, config);
      } else {
        await API.post("/admin/provision", payload, config);
      }

      handleResetForm();
      await fetchSystemUsers();
      setSaving(false);
      setSaveSuccess(wasEditing ? "User updated successfully!" : "New user created successfully!");

    } catch (err) {
      setSaving(false);
      setSaveError(err.response?.data?.message || "Internal database connection failure.");
    }
  };

 const handleResetForm = () => {
    setFullName(""); setPhone(""); setEmail(""); setRole("engineer");
    setAddress(""); setIsApprover(false); setUnit([]); setRegion([]);
    setAdminAccess(false); setEditingId(null); setShowForm(false); setDashboardType("");
    setSaveError("");
    setSaveSuccess("");
  };

  const handleEdit = (user) => {
    setFullName(user.name || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
    setAddress(user.address || "");
    setRole(user.role ? user.role.toLowerCase() : "");
    setIsApprover(!!user.isApprover);
   const parsedRegions = (user.region || "").split(",").map(r => r.trim()).filter(Boolean);
    setRegion(parsedRegions);
    setUnitOptions(parsedRegions.flatMap(r => REGION_UNIT_MAP[r] || []));
    const parsedUnits = (user.unit || "").split(",").map(u => u.trim()).filter(Boolean);
    setUnit(parsedUnits);
    setAdminAccess(!!user.is_admin);
    setDashboardType(user.dashboard_type || "engineer");
    setEditingId(user.id);
    setShowForm(true);
    setSaveError("");
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDelete = (id) => {
    setDeleteModal({ open: true, userId: id });
  };

  const [loggingInAsId, setLoggingInAsId] = useState(null);

  const handleLoginAs = async (targetUser) => {
    if (!targetUser || !targetUser.id) return;

    const currentUserId = localStorage.getItem("id");
    if (String(currentUserId) === String(targetUser.id)) {
      setSaveSuccess(`You are already logged in as ${targetUser.name || "this user"}.`);
      return;
    }

    setLoggingInAsId(targetUser.id);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await API.post("/auth/login-as", { userId: targetUser.id }, config);

      if (res.data?.success) {
        const { token: newToken, user: profileData } = res.data;

        // Remember original admin email in sessionStorage if not already present
        if (!sessionStorage.getItem("original_admin_email")) {
          sessionStorage.setItem("original_admin_email", localStorage.getItem("email") || "");
        }

        localStorage.setItem("profileData", JSON.stringify(profileData));
        localStorage.setItem("user", JSON.stringify(profileData));
        localStorage.setItem("token", newToken);
        localStorage.setItem("role", profileData.role || "");
        localStorage.setItem("name", profileData.name || "");
        localStorage.setItem("email", profileData.email || "");
        localStorage.setItem("status", profileData.status || "");
        localStorage.setItem("id", profileData.id || "");
        localStorage.setItem("project_id", profileData.project_id ?? "");
        localStorage.setItem("manager_id", profileData.manager_id ?? "");
        localStorage.setItem("isApprover", Number(profileData.isApprover));
        localStorage.setItem("region", profileData.region || "");
        localStorage.setItem("type", profileData.dashboard_type || "");
        localStorage.setItem("is_admin", profileData.is_admin ?? 0);

        const is_admin = Number(profileData.is_admin);
        const type = (profileData.dashboard_type || "").toLowerCase().trim();
        const role = (profileData.role || "").toLowerCase().trim();

        if (role === "client") {
          window.location.href = "/client-dashboard";
        } else if (is_admin === 1 || type === "admin" || role === "admin" || role === "manager") {
          window.location.href = "/admin-dashboard";
        } else {
          window.location.href = "/engineer";
        }
      } else {
        setSaveError(res.data?.message || "Failed to switch user.");
        setLoggingInAsId(null);
      }
    } catch (err) {
      console.error("Login-as failed:", err);
      setSaveError(err.response?.data?.message || "Failed to login as user.");
      setLoggingInAsId(null);
    }
  };

  const isAdmin = Number(localStorage.getItem("is_admin"));
  const userType = localStorage.getItem("type");

  // const [region, setRegion] = useState(() => {
  //   // Super Admin -> keep region blank
  //   if (isAdmin === 1) return "";

  //   // Admin (not super admin) -> use region from localStorage
  //   if (userType === "admin") {
  //     return localStorage.getItem("region") || "";
  //   }

  //   return "";
  // });

  const [region, setRegion] = useState(() => {
    // Super Admin -> keep region blank
    if (isAdmin === 1) return [];

    // Admin (not super admin) -> use region from localStorage
    if (userType === "admin") {
      const stored = localStorage.getItem("region") || "";
      return stored ? stored.split(",").map(r => r.trim()).filter(Boolean) : [];
    }

    return [];
  });

  // const [unitOptions, setUnitOptions] = useState(
  //   REGION_UNIT_MAP[region] || []
  // );
  const [unitOptions, setUnitOptions] = useState(
    region.flatMap(r => REGION_UNIT_MAP[r] || [])
  );

//   useEffect(() => {
//   if (region) {
//     const matchedUnits = REGION_UNIT_MAP[region] || [];
//     setUnitOptions(matchedUnits);
//   }
// }, [region]);

useEffect(() => {
  if (region.length > 0) {
    const merged = Array.from(new Set(region.flatMap(r => REGION_UNIT_MAP[r] || [])));
    setUnitOptions(merged);
  } else {
    setUnitOptions([]);
  }
}, [region]);


  useEffect(() => {
    if (isAdmin === 0) {
      setDashboardType("engineer");
    }
  }, [isAdmin]);

  const adminCount = usersList.filter(u => (u.role || "").toLowerCase() === "admin").length;
const engineersCount = usersList.filter(u => (u.role || "").toLowerCase() === "engineer").length;
const clientsCount = usersList.filter(u => (u.role || "").toLowerCase() === "client").length;

  const roles = ["All", ...Array.from(
  new Map(
    usersList
      .map(u => getDisplayRole(u))
      .filter(Boolean)
      .map(r => r.trim())
      .filter(Boolean)
      .map(r => [r.toLowerCase(), r.charAt(0).toUpperCase() + r.slice(1).toLowerCase()])
  ).values()
)];
  const regions = ["All", ...Object.keys(REGION_UNIT_MAP)];
const units = regionFilter === "All"
  ? ["All", ...Array.from(new Set(usersList.map(u => u.unit).filter(Boolean)))]
  : ["All", ...(REGION_UNIT_MAP[regionFilter] || [])];
const filtered = usersList.filter(u => {
  const q = search.toLowerCase();
  const matchSearch = !q
    || (u.name || "").toLowerCase().includes(q)
    || (u.email || "").toLowerCase().includes(q)
    || (u.role || "").toLowerCase().includes(q);
  const matchRole = roleFilter === "All" || (getDisplayRole(u) || "").toLowerCase() === roleFilter.toLowerCase();
 const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
const matchRegion = regionFilter === "All" || (u.region || "").split(",").map(r => norm(r)).includes(norm(regionFilter));
const matchUnit = unitFilter === "All" || (u.unit || "").split(",").map(s => norm(s)).includes(norm(unitFilter));
  return matchSearch && matchRole && matchRegion && matchUnit;
});
  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
const pageRows = filtered.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

 

  const DeleteModal = () => {
    if (!deleteModal.open) return null;
    return (
<div style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "rm-modal-fadein 0.18s ease",
      }}
      >
        <div
          style={{
            background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
            boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
            border: `1px solid ${T.borderSolid}`, overflow: "hidden",
          }}
        >
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red})` }} />
          <div style={{ padding: "28px 28px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
<div style={{
                width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                background: T.redLight, border: `1.5px solid #FECDD3`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}><Trash2 size={22} /></div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>
                Delete User
              </div>
            </div>
<p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
  Are you sure you want to delete this user? This action cannot be undone.
</p> 
            
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteModal({ open: false, userId: null })}
                style={{
                  padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                  background: T.surfaceAlt, color: T.muted,
                  border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={async () => {
                  const id = deleteModal.userId;
                  setDeleteModal({ open: false, userId: null });
                  try {
                    const token = localStorage.getItem("token");
                    const config = { headers: { Authorization: `Bearer ${token}` } };
                    const res = await API.delete(`/admin/users/${id}`, config);
                    if (res.data.success) {
                      await fetchSystemUsers();
                      setSaveSuccess("User deleted successfully!");
                    }
                  } catch { setSaveError("Failed to delete user profile."); }
                }}
                style={{
                  padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                  background: `linear-gradient(135deg,${T.red},#E02040)`,
                  color: "#fff", border: "none", cursor: "pointer",
                  boxShadow: `0 4px 14px ${T.red}40`,
                }}
              >Delete</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <DeleteModal />

      {/* ── SUCCESS TOAST (top-right, auto-dismisses) ── */}
      {saveSuccess && (
        <div style={{
          position: "fixed", top: 24, right: 24, zIndex: 99999,
          background: T.green, color: "#fff",
          padding: "14px 18px", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(26,122,66,0.35)",
          display: "flex", alignItems: "center", gap: 12,
          minWidth: 280, maxWidth: 380,
          animation: "dtm-modal-in 0.22s ease",
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}> </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{saveSuccess}</div>
            <div style={{ fontSize: 12, opacity: 0.82, marginTop: 2 }}>User list has been refreshed.</div>
          </div>
          <button onClick={() => setSaveSuccess("")} style={{
            background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
            width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
            fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>✕</button>
        </div>
      )}

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href={FONT_LINK} rel="stylesheet" />

      <style>{`
      .um-root *, .um-root *::before, .um-root *::after {
          box-sizing: border-box;
          font-family: 'Montserrat', sans-serif !important;
        }
        .um-row:hover { background: ${T.violetFaint} !important; }
        .um-pill { transition: all 0.15s ease !important; }
        .um-pill:hover { transform: translateY(-1px); }
        .um-add-btn:hover {
          box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important;
          transform: translateY(-1px);
        }
        .um-add-btn { transition: all 0.18s ease !important; }
        .um-refresh:hover { opacity: 0.82; }
        .um-refresh { transition: opacity 0.15s; cursor: pointer; }
        .um-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.10) !important;
        }
        @media (max-width: 1024px) {
          .um-kpi-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 768px) {
          .um-header     { flex-direction: column !important; gap: 14px !important; }
          .um-kpi-grid   { grid-template-columns: repeat(2,1fr) !important; }
          .um-filter-row { flex-direction: column !important; align-items: flex-start !important; }
          .um-table-wrap { font-size: 12px !important; }
          .um-hide-md    { display: none !important; }
        }
        @media (max-width: 480px) {
          .um-kpi-grid  { grid-template-columns: 1fr 1fr !important; }
          .um-page-pad  { padding: 14px 12px !important; }
          .um-hide-sm   { display: none !important; }
          .um-form-grid { grid-template-columns: 1fr !important; }
        }
::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: ${T.faint}; border-radius: 4px; }
        @keyframes um-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes dtm-modal-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }
        @keyframes rm-modal-fadein { from { opacity:0; transform:scale(0.94); } to { opacity:1; transform:scale(1); } }
      `}</style>

      <div className="um-root um-page-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="um-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · User Management
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                User Master
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>ADMIN</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Manage all system user accounts and roles
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
  type="button"
  className="um-refresh"
  onClick={async () => {
    setRefreshing(true);
    setUsersList([]);
    const minSpin = new Promise(resolve => setTimeout(resolve, 600));
    await Promise.all([fetchSystemUsers(), minSpin]);
    setRefreshing(false);
  }}
  title="Refresh"
  disabled={refreshing}
  style={{
    width: 40, height: 40, borderRadius: 11,
    border: `1.5px solid ${T.borderSolid}`,
    background: T.surface, color: T.muted,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, flexShrink: 0, cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
  }}
>
<span style={{
    display: "inline-flex",
    animation: refreshing ? "um-spin 0.7s linear infinite" : "none",
  }}><RefreshCw size={16} /></span>
</button>

   
            <button
              onClick={() => {
               const headers = ["#", "Name", "Email", "Phone", "Region", "Unit", "Approver"];
const rows = filtered.map((u, i) => [
  i + 1,
  u.name || "",
  u.email || "",
  u.phone || "",
  u.region || "",
  u.unit || "",
  u.isApprover == 1 ? "Yes" : "No"
]);
                const csvContent = [headers, ...rows]
                  .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                  .join("\n");
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `users_export_${new Date().toISOString().slice(0,10)}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
              style={{ height: 38, padding: "0 18px", borderRadius: 10, border: `1.5px solid ${T.violet}`, background: T.violetLight, color: T.violet, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
            >
               Export 
            </button>

            <button className="um-add-btn"
              onClick={() => { if (showForm) { handleResetForm(); } else { setShowForm(true); } }}
              style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none",
                padding: "11px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: `0 4px 14px ${T.violet}40`,
                cursor: "pointer", whiteSpace: "nowrap",
              }}>
<Plus size={16} />
              {editingId ? "Edit User" : showForm ? "Close Form" : "Add User"}
            </button>
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div className="um-kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
{[
 { label: "Total Users", value: usersList.length, icon: <Users size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
  
].map((s) => (
            <div key={s.label} style={{
              background: T.surface, borderRadius: 16, border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px", display: "flex", alignItems: "center", gap: 14,
              boxShadow: T.shadow, position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", right: -10, top: -10, width: 70, height: 70, borderRadius: "50%", background: s.bg, opacity: 0.6 }} />
              <div style={{ width: 46, height: 46, borderRadius: 13, background: s.bg, border: `1.5px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, position: "relative" }}>
                {s.icon}
              </div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── COLLAPSIBLE FORM ── */}
{showForm && (
  <div ref={formRef} style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", marginBottom: 24, boxShadow: T.shadowMd, scrollMarginTop: 100 }}>
            <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
            <div style={{ padding: "22px 26px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.violetLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
{editingId ? <Pencil size={16} /> : <Plus size={18} />}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>{editingId ? "Edit User Record" : "Create New User"}</div>
                  <div style={{ fontSize: 12, color: T.muted }}>Fill in the details — fields marked * are required</div>
                </div>
              </div>

              <div className="um-form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={LabelSty}>Full Name *</label>
                  <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Rahul Sharma"
                    onFocus={fIn("name")} onBlur={fOut("name")} style={inputSty(foc.name)} />
                </div>
                <div>
                  <label style={LabelSty}>Email Address *</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@company.com"
                    onFocus={fIn("email")} onBlur={fOut("email")} style={inputSty(foc.email)} />
                </div>
                <div>
                  <label style={LabelSty}>Phone Number</label>

                  {/* validation change */}

                  <input
                    value={phone}
                    maxLength={10}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setPhone(value);
                    }}
                    placeholder="9876543210"
                    onFocus={fIn("phone")}
                    onBlur={fOut("phone")}
                    style={inputSty(foc.phone)}
                  />
                </div>
               {/* System Role field hidden — role defaults to "engineer" internally */}
<MultiSearchDropdown
                  label="Region"
                  values={region}
                  onChange={(newRegions) => {
                    setRegion(newRegions);

                    const merged = Array.from(new Set(newRegions.flatMap(r => REGION_UNIT_MAP[r] || [])));
                    setUnitOptions(merged);

                   setUnit(prev => prev.filter(u => merged.includes(u)));
                  }}
                  options={regionOptions}
                  placeholder="Search region…"
                />
              <MultiSearchDropdown label="Unit" values={unit} onChange={setUnit} options={unitOptions} placeholder={region.length > 0 ? "Search unit…" : "Select a Region first"} />
                <div>
                  <label style={LabelSty}>Dashboard Type</label>
                  <select
                    value={isAdmin === 0 ? "engineer" : dashboardType}
                    onChange={(e) => setDashboardType(e.target.value)}
                    onFocus={fIn("dashboardType")}
                    onBlur={fOut("dashboardType")}
                    disabled={isAdmin === 0}
                    style={{
                      ...inputSty(foc.dashboardType),
                      height: 42,
                      backgroundColor: isAdmin === 0 ? "#f5f5f5" : "",
                      cursor: isAdmin === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    <option value="">Select Dashboard</option>
                    <option value="admin">Admin Dashboard</option>
                    <option value="engineer">Engineer Dashboard</option>
                  </select>
                </div>
              </div>

              {/* <div style={{ marginBottom: 16 }}>
                <label style={LabelSty}>Address</label>
                <textarea rows={3} value={address} onChange={e => setAddress(e.target.value)} placeholder="Full address"
                  onFocus={fIn("address")} onBlur={fOut("address")}
                  style={{ ...inputSty(foc.address), resize: "vertical" }} />
              </div> */}

              {isAdmin === 1 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 28, marginBottom: 22, padding: "14px 16px", background: T.surfaceAlt, borderRadius: 12, border: `1px solid ${T.borderSolid}` }}>
                  <Toggle checked={isApprover} onChange={setIsApprover} label="Is Approver" />
                  <Toggle checked={adminAccess} onChange={setAdminAccess} label="Admin Access" />
                </div>
              )}

              {/* ── ERROR BANNER ── */}
              {saveError && (
                <div style={{
                  marginBottom: 14, padding: "12px 16px", borderRadius: 10,
                  background: "#FEF2F2", border: `1.5px solid #FECACA`,
                  display: "flex", alignItems: "flex-start", gap: 10,
                }}>
<span style={{ flexShrink: 0, display: "inline-flex" }}><AlertTriangle size={16} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 2 }}>Could not save user</div>
                    <div style={{ fontSize: 12, color: "#B91C1C", lineHeight: 1.5 }}>{saveError}</div>
                  </div>
<button onClick={() => setSaveError("")} style={{
                    marginLeft: "auto", background: "none", border: "none",
                    color: "#DC2626", cursor: "pointer", padding: 0, flexShrink: 0, display: "flex",
                  }}><X size={14} /></button>
                </div>
              )}

              {/* ── BUTTONS ── */}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  style={{
                    background: saving
                      ? `linear-gradient(135deg,${T.muted},#9E8BB5)`
                      : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                    color: "#fff", border: "none", padding: "11px 24px", borderRadius: 11,
                    fontSize: 13, fontWeight: 700,
                    cursor: saving ? "not-allowed" : "pointer",
                    boxShadow: saving ? "none" : `0 4px 14px ${T.violet}40`,
                    transition: "all 0.18s",
                    display: "flex", alignItems: "center", gap: 8,
                    opacity: saving ? 0.85 : 1,
                  }}
                >
                  {saving ? (
                    <>
                      <span style={{
                        display: "inline-block", width: 14, height: 14,
                        border: "2px solid rgba(255,255,255,0.35)",
                        borderTopColor: "#fff", borderRadius: "50%",
                        animation: "um-spin 0.7s linear infinite",
                      }} />
                      Please wait…
                    </>
                  ) : (
<> {editingId ? "Update User" : "Save User"}</>
                  )}
                </button>
                <button onClick={handleResetForm} disabled={saving} style={{
                  background: T.surfaceAlt, color: T.muted,
                  border: `1.5px solid ${T.borderSolid}`, padding: "11px 20px",
                  borderRadius: 11, fontSize: 13, fontWeight: 600,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.5 : 1,
                }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── TABLE CARD ── */}
        <div style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(92,45,145,0.08)" }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />

          <div style={{ padding: "20px 24px" }}>
            <div className="um-filter-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>Existing Users</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filtered.length} of {usersList.length} user profiles
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {roles.slice(0, 6).map(r => (
                    <button key={r} className="um-pill" onClick={() => { setRoleFilter(r); setCurrentPage(1); }} style={{
                      fontSize: 11, padding: "6px 14px", borderRadius: 20, fontWeight: 600,
                      border: `1.5px solid ${roleFilter === r ? T.violet : T.borderSolid}`,
                      background: roleFilter === r ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface,
                      color: roleFilter === r ? "#fff" : T.muted,
                      cursor: "pointer",
                      boxShadow: roleFilter === r ? `0 2px 8px ${T.violet}30` : "none",
                      textTransform: "capitalize",
                    }}>{r}</button>
                  ))}
                                </div>

                {/* Region + Unit Filter Group */}
                <div style={{ display: "flex", alignItems: "center", gap: 0, border: `1.5px solid ${T.borderSolid}`, borderRadius: 10, overflow: "hidden", background: T.surface }}>
                  <select
                    value={regionFilter}
                    onChange={e => { setRegionFilter(e.target.value); setUnitFilter("All"); setCurrentPage(1); }}
                    style={{ height: 36, border: "none", borderRight: `1.5px solid ${T.borderSolid}`, padding: "0 12px", fontSize: 13, color: T.dark, background: "transparent", cursor: "pointer", outline: "none", width: 150, maxWidth: 150, flexShrink: 0, textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}
                  >
                    {regions.map(r => <option key={r} value={r}>{r === "All" ? "All Regions" : r}</option>)}
                  </select>
                  <select
                    value={unitFilter}
                    onChange={e => { setUnitFilter(e.target.value); setCurrentPage(1); }}
                    style={{ height: 36, border: "none", padding: "0 12px", fontSize: 13, color: T.dark, background: "transparent", cursor: "pointer", outline: "none", width: 150, maxWidth: 150, flexShrink: 0, textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}
                  >
                    {units.map(u => <option key={u} value={u}>{u === "All" ? "All Units" : u}</option>)}
                  </select>
                </div>

                <div className="um-search" style={{ display: "flex", alignItems: "center", gap: 8, background: T.surfaceAlt, border: `1.5px solid ${T.borderSolid}`, borderRadius: 12, padding: "9px 14px", minWidth: 240, transition: "all 0.2s" }}>
                  <Search size={15} color={T.faint} />
                  <input type="text" placeholder="Search name, email, role…" value={search}
                   onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                    style={{ border: "none", background: "transparent", fontSize: 13, color: T.dark, outline: "none", width: "100%", fontWeight: 400 }} />
                  {search && (
                    <button onClick={() => setSearch("")} style={{ background: T.violetLight, border: "none", color: T.violet, cursor: "pointer", fontSize: 11, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>✕</button>
                  )}
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "64px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: `2px dashed ${T.faint}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>👤</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark, marginBottom: 6 }}>
                    {search ? "No matching users" : "No users yet"}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, maxWidth: 300, lineHeight: 1.6 }}>
                    {search ? `No results for "${search}". Try a different search.` : "Click '+ Add User' to create the first user."}
                  </div>
                  {search && (
                    <button onClick={() => setSearch("")} style={{ marginTop: 14, background: `linear-gradient(135deg,${T.violet},${T.violetMid})`, color: "#fff", border: "none", padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Clear Search</button>
                  )}
                </div>
              </div>
            ) : (
              <div className="um-table-wrap" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 760 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {[
                        { label: "#", center: true, cls: "" },
                        { label: "User", center: false, cls: "" },
                        { label: "Contact", center: false, cls: "" },
                        { label: "Role", center: false, cls: "um-hide-sm" },
                        { label: "Unit / Region", center: false, cls: "um-hide-md" },
                        { label: "Status", center: false, cls: "um-hide-md" },
                        { label: "Approver", center: false, cls: "" },
                        { label: "Actions", center: false, cls: "" },
                      ].map((h) => (
                        <th key={h.label} className={h.cls} style={{
                          fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          padding: "12px 16px", textAlign: h.center ? "center" : "left",
                          borderBottom: `2px solid ${T.borderSolid}`,
                          borderTop: `1px solid ${T.borderSolid}`,
                          whiteSpace: "nowrap", background: T.violetFaint,
                        }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    
                  {pageRows.map((user, i) => {
const displayRole = ((user.role || "").toLowerCase() === "engineer" && (user.dashboard_type || "").toLowerCase() === "admin")
  ? "Manager"
  : user.role;
const cfg = roleCfg(displayRole);
                      const isSuspended = (user.status || "").toLowerCase() === "suspended";
                      const isLast = i === filtered.length - 1;
                      return (
                        <tr key={user.id} className="um-row"
                          onMouseEnter={() => setHov(user.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{ background: T.surface, transition: "background 0.12s", cursor: "default" }}
                        >
                          <td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, background: T.violetFaint, fontWeight: 700, fontSize: 11, color: T.muted }}>{(currentPage - 1) * ROWS_PER_PAGE + i + 1}</span>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar
                                name={user.name || "?"}
                                role={user.role}
                                size={36}
                                clickable={true}
                                loading={loggingInAsId === user.id}
                                onClick={() => handleLoginAs(user)}
                                title={`Click to login as ${user.name || "this user"}`}
                              />
                              <div style={{ fontSize: 14, fontWeight: 700, color: T.dark }}>{user.name || "—"}</div>
                            </div>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.dark }}>{user.email}</div>
                            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{user.phone || "—"}</div>
                          </td>
                          <td className="um-hide-sm" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{ display: "inline-block", background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20, textTransform: "capitalize" }}>
                             {displayRole || "—"}
                            </span>
                          </td>
                          <td className="um-hide-md" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.dark }}>{user.unit || "—"}</div>
                            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{user.region || "—"}</div>
                          </td>
                          <td className="um-hide-md" style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20,
                              background: isSuspended ? "#FFF1F2" : T.greenLight,
                              color: isSuspended ? "#E11D48" : T.green,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
                              {isSuspended ? "Suspended" : "Active"}
                            </span>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-block", fontSize: 11, fontWeight: 700,
                              padding: "5px 12px", borderRadius: 20,
                              background: user.isApprover ? T.greenLight : "#FEF2F2",
                              color: user.isApprover ? T.green : "#DC2626",
                              border: `1px solid ${user.isApprover ? "#A7F3D0" : "#FECACA"}`,
                            }}>
                              {user.isApprover ? "Yes" : "No"}
                            </span>
                          </td>
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                             <button
  onClick={() => handleEdit(user)}
  style={{ background: T.violetLight, color: T.violet, border: `1px solid ${T.borderSolid}`, padding: "7px 14px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center" }}
  onMouseEnter={e => { e.currentTarget.style.background = T.violet; e.currentTarget.style.color = "#fff"; }}
  onMouseLeave={e => { e.currentTarget.style.background = T.violetLight; e.currentTarget.style.color = T.violet; }}
>
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>
  </svg>
</button>
<button
  onClick={() => handleDelete(user.id)}
  style={{ background: T.redLight, color: T.red, border: `1px solid #F8C8D0`, padding: "7px 14px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center" }}
  onMouseEnter={e => { e.currentTarget.style.background = T.red; e.currentTarget.style.color = "#fff"; }}
  onMouseLeave={e => { e.currentTarget.style.background = T.redLight; e.currentTarget.style.color = T.red; }}
>
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            
{filtered.length > 0 && (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.borderSolid}`, flexWrap: "wrap", gap: 12 }}>
    <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
      Showing <strong style={{ color: T.dark }}>{Math.min((currentPage-1)*ROWS_PER_PAGE+1, filtered.length)}–{Math.min(currentPage*ROWS_PER_PAGE, filtered.length)}</strong> of <strong style={{ color: T.dark }}>{filtered.length}</strong> users
    </span>
    <div style={{ display: "flex", gap: 6 }}>
      <button
        onClick={() => setCurrentPage(p => Math.max(1, p-1))}
        disabled={currentPage === 1}
        style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${currentPage===1 ? T.borderSolid : T.violet}`, background: currentPage===1 ? T.surface : T.violetLight, color: currentPage===1 ? T.faint : T.violet, cursor: currentPage===1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}
      >‹ Prev</button>
      {Array.from({ length: totalPages }, (_, i) => i+1)
        .slice(Math.max(0, currentPage-3), Math.min(totalPages, currentPage+2))
        .map(n => (
          <button key={n} onClick={() => setCurrentPage(n)}
            style={{ width: 34, height: 34, borderRadius: 10, border: `1.5px solid ${n===currentPage ? T.violet : T.borderSolid}`, background: n===currentPage ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface, color: n===currentPage ? "#fff" : T.faint, fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: n===currentPage ? `0 2px 8px ${T.violet}40` : "none" }}
          >{n}</button>
        ))}
      <button
        onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))}
        disabled={currentPage === totalPages}
        style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${currentPage===totalPages ? T.borderSolid : T.violet}`, background: currentPage===totalPages ? T.surface : T.violetLight, color: currentPage===totalPages ? T.faint : T.violet, cursor: currentPage===totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}
      >Next ›</button>
    </div>
  </div>
)}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}